import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSingleCustomCollection from '@/hooks/useSingleCustomCollection'

const formatValue = value => {
	if (value === null || value === undefined || value === '') return '—'
	if (Array.isArray(value)) return value.join(', ')
	if (typeof value === 'object') return JSON.stringify(value)
	return String(value)
}

export default function CustomLookupFieldPicker({ onSelect, collectionId }) {
	const { t } = useTranslation()
	// console.log(`TCL -> CustomLookupFieldPicker.jsx:12 -> CustomLookupFieldPicker -> collectionId:`, collectionId)
	const [searchTerm, setSearchTerm] = useState('')
	const searchInputRef = useRef(null)

	const {
		items,
		collectionName,
		collectionDetails,
		loading,
		loadingCollection,
		loadingMore,
		error,
		hasMore,
		initializeCollection,
		loadItems,
		handleLoadMore,
		updateFilters,
	} = useSingleCustomCollection(collectionId)
	// console.log(`TCL -> CustomLookupFieldPicker.jsx:29 -> CustomLookupFieldPicker -> collectionDetails:`, collectionDetails)

	useEffect(() => {
		if (collectionId) {
			initializeCollection()
		}
	}, [collectionId, initializeCollection])

	useEffect(() => {
		if (!loadingCollection && searchInputRef.current) {
			searchInputRef.current.focus()
		}
	}, [loadingCollection, collectionId])

	useEffect(() => {
		if (collectionName) {
			loadItems()
		}
	}, [collectionName, loadItems])

	const columns = useMemo(() => {
		const schemaFields = (collectionDetails?.fields || []).map(field => field.name).filter(Boolean)
		return schemaFields
	}, [items, collectionDetails])

	const handleSearchChange = useCallback(
		e => {
			const value = e.target.value
			setSearchTerm(value)

			if (value.length >= 3 || value.length === 0) {
				updateFilters({ searchText: value })
			}
		},[updateFilters]
	)

	const handleKeyDown = e => {
		if (e.key === 'Enter') {
			e.preventDefault()
			if (items.length === 1) {
				onSelect(items[0])
			}
		}
	}

	if (loadingCollection) {
		return <div className="text-center py-4 text-gray-500">{t('singleCustomCollection.form.lookup.loadingCollection')}</div>
	}

	if (!collectionName) {
		return <div className="text-center py-4 text-gray-500">{t('singleCustomCollection.form.lookup.collectionUnavailable')}</div>
	}

	return (
		<div className="space-y-4">
			<div className="mb-4">
				<input
					ref={searchInputRef}
					type="text"
					value={searchTerm}
					onChange={handleSearchChange}
					onKeyDown={handleKeyDown}
					id="searchBox"
					placeholder={t('singleCustomCollection.form.lookup.searchPlaceholder', { name: collectionName })}
					className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
				/>
			</div>

			{error && (
				<div className="bg-red-50 border border-red-200 rounded-md p-3">
					<div className="text-sm text-red-600">{t('singleCustomCollection.form.lookup.errorTitle')}: {String(error)}</div>
				</div>
			)}

			{loading ? (
				<div className="text-center py-4 text-gray-500">{t('singleCustomCollection.form.lookup.loadingItems')}</div>
			) : items.length === 0 ? (
				<div className="text-center py-4 text-gray-500">{t('singleCustomCollection.form.lookup.noItems')}</div>
			) : (
				<div className="overflow-x-auto">
					<table className="min-w-full bg-white border border-gray-300 rounded-lg shadow">
						<thead>
							<tr className="bg-gray-50">
								{columns.map(col => (
									<th key={col} className="py-3 px-4 border-b border-gray-300 text-left font-semibold text-gray-700">{col}</th>
								))}
								<th className="py-3 px-4 border-b border-gray-300 text-left w-24 font-semibold text-gray-700">{t('singleCustomCollection.form.lookup.action')}</th>
							</tr>
						</thead>
						<tbody>
							{items.map(item => {
								const key = item._id || item.id || JSON.stringify(item)
								return (
									<tr key={key} className="hover:bg-gray-50">
										{columns.map(col => (
											<td key={`${key}-${col}`} className="py-3 px-4 border-b border-gray-300">
												<span className="text-gray-900 text-sm">{formatValue(col === '_id' ? item._id || item.id : item[col])}</span>
											</td>
										))}
										<td className="py-3 px-4 border-b border-gray-300">
											<span
												role="button"
												tabIndex={0}
												onClick={() => onSelect(item)}
												onKeyPress={e => { if (e.key === 'Enter' || e.key === ' ') onSelect(item) }}
												className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer inline-block"
											>
												{t('singleCustomCollection.form.lookup.select')}
											</span>
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			)}

			{hasMore && (
				<div className="text-center">
					<button
						type="button"
						onClick={handleLoadMore}
						className="px-4 py-2 text-sm font-medium text-blue-700 border border-blue-200 rounded-md hover:bg-blue-50 disabled:opacity-60"
						disabled={loadingMore}
					>
						{loadingMore ? t('loadMore.loading') : t('loadMore.button')}
					</button>
				</div>
			)}
		</div>
	)
}
